import type { ReactNode } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMR } from '../../urbanmap/_components';
import type { ReviewBrick } from './wireframe.types';

const space = 'var(--nj-semantic-size-spacing-12)';
export function ReviewBlock({ title, children }: { title: string; children: ReactNode }) {
  return <section style={{ border: `1px solid ${UMR.border}`, background: UMR.grey100, padding: space, minWidth: 0 }}>
    <NJText as="h3" scale="md" style={{ margin: 0 }}>{title}</NJText>
    <NJText scale="sm">{children}</NJText>
  </section>;
}

export const BRICKS: { id: ReviewBrick; label: string }[] = [
  { id: 'buildings', label: 'Bâtiments' }, { id: 'trace', label: 'Tracé' },
  { id: 'phasing', label: 'Phasage' }, { id: 'recap', label: 'Récapitulatif' },
];
const FAMILIES: Record<ReviewBrick, string[]> = {
  buildings: ['Demande', 'Importance des clients'], trace: ['Tracé', 'Points de départ'],
  phasing: ['Développement', 'Phasage'], recap: [],
};

export function ReviewPlanSurface({ brick, full, onFamily, onSst, onExport, canWrite }: {
  brick: ReviewBrick; full: boolean; onFamily: (family: string) => void;
  onSst: () => void; onExport: () => void; canWrite: boolean;
}) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: space }}>
    {FAMILIES[brick].length > 0 && <ReviewBlock title="Jeux choisis dans cette section">
      {FAMILIES[brick].map((family) => <span key={family} style={{ display: 'block' }}>
        {family} : [nom du jeu · version · choix à confirmer]
        <NJButton label={`Gérer · ${family}`} variant="secondary" emphasis="minimal" scale="sm" onClick={() => onFamily(family)} />
      </span>)}
      Les valeurs se modifient uniquement dans Hypothèses ; aucun formulaire dupliqué ici.
    </ReviewBlock>}
    {brick === 'buildings' && <>
      <ReviewBlock title="Résumé Bâtiments">[Nombre de bâtiments] · [Consommation totale · MWh/an]. Aucun autre KPI.</ReviewBlock>
      <ReviewBlock title="Composition à confirmer">Sélections sources cumulées, union sans doublons, ajouts/retraits individuels.
        {!full && ' Carte : affinage un par un / lasso existant.'}
        {' '}Annuler conserve la composition confirmée ; Confirmer valide les modifications.
      </ReviewBlock>
      <NJButton label="Configurer les SST au niveau projet" variant="secondary" scale="sm" onClick={onSst} />
      {full && <ReviewBlock title="Trois graphiques + légendes (emplacements)">
        MWh/an par usage · surface (m²) par usage · ancienne énergie de chauffage (fioul, gaz…).
        Données absentes : « Non renseigné », pas de valeurs inventées.
      </ReviewBlock>}
    </>}
    {brick === 'trace' && <>
      <ReviewBlock title="Actions locales du Tracé">
        [Lancer le tracé — simulation] puis [Calculer la densité — simulation].
        Le calcul de densité annonce la correction automatique de géométrie simulée. Pas de calcul global.
      </ReviewBlock>
      <ReviewBlock title="Résultat et légendes">[Longueur · km] · [Densité · MWh/m/an].
        Carte : légende des routes interdites, obligatoires et poids appliqués. Détail DN exclu.
      </ReviewBlock>
    </>}
    {brick === 'phasing' && <>
      <ReviewBlock title="Actions locales du Phasage">
        [Phasage auto — simulation] · [Phaser les canalisations d'après les dates bâtiments].
        Dates manuelles possibles et conservées ; remplacement explicite, jamais silencieux.
      </ReviewBlock>
      <ReviewBlock title={full ? 'Tableaux et graphiques (emplacements)' : 'Tableaux synchronisés à la carte (emplacements)'}>
        Bâtiments : identifiant, adresse, usage, consommation, année. Canalisations : tronçon, année, mode.
        Tri/filtres conservés. Import CSV avec aperçu/confirmation ; export via Import-export.
        {full
          ? ' Graphiques légendés : consommation par an (cumul/non cumul), bâtiments par an, km par an.'
          : ' Sélection unitaire/lasso, affectation de date au groupe ; clic de consultation sans modification.'}
      </ReviewBlock>
    </>}
    {brick === 'recap' && <>
      <ReviewBlock title="Récapitulatif complet">
        Configuration projet et SST, valeurs/versions des six familles, sources/composition, tracé et phases.
        Courant distinct du dernier calcul archivé ; archive absente explicitée, jamais reconstituée.
      </ReviewBlock>
      <NJButton label="Ouvrir Import-export avec ce plan présélectionné" variant="secondary" scale="sm" onClick={onExport} />
    </>}
    {brick !== 'recap' && !full && <ReviewBlock title="Zone carte — bloc gris de revue, pas une carte géographique">
      Réutiliser UMMap/Gentilly à l'intégration. Légendes et outils existants conservés, un seul plan actif.
      Aucun tracé ni calcul exécuté dans cette basse fidélité.
    </ReviewBlock>}
    <ReviewBlock title="Confirmation et états (contrat, non simulés ici)">
      {canWrite ? 'Écriture : modifications non confirmées → Annuler / Confirmer.' : 'Lecture : consultation seule, aucune écriture ni calcul.'}
      {' '}Calculé : bâtiments confirmés + tracé fait + densité calculée. En cours : file/calcul.
      Échec : vrai motif du calcul lancé. Obsolète : changements et aval à relancer.
      Retirer un prospect met à jour consommation/densité dérivable sans calcul artificiel.
    </ReviewBlock>
  </div>;
}
